import { useMemo, type ReactNode } from 'react'
import { Pencil, ThumbsUp } from 'lucide-react'
import { Button, ButtonGroup } from '@highlight/ui'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import { ActionTagArea } from '@renderer/components/action-tags'
import { applyMetadataTransitions, visibleNestedViews } from '@renderer/components/action-tags/status-transition'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { NestedView } from '@renderer/components/nested-view'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import {
  answerableOptionGroups,
  askUserCardVariant,
  getAnswerableFraming,
} from '@renderer/chat/integrations/answerable'
import { AskUserBadge, AskUserOptionsPreview } from '@renderer/chat/integrations/ask-user-card'
import { getReportOriginSkill, OriginSkillIcon } from '@renderer/chat/integrations/report-to-user'
import { ReconnectButton } from './action-connection-error'
import { CellPreviewCard, HOVER_CARD_TRIGGER, usePinnedDisclosure } from './cell-preview-card'
import { NO_NESTED_VIEWS } from '../hooks/staged-actions/empty'
import { ActionSource, hasActionSourceLabel } from './action-source'
import { PrerequisiteCtaButton } from '../hooks/staged-actions/use-connection-prerequisite'
import { trackStagedActionCopy, trackStagedActionOpenLink } from '../hooks/staged-actions/track-staged-action'
import type { StagedAction, StagedActionView } from '../hooks/use-staged-actions'
import { feedbackSubject } from '@renderer/chat/api/feedback'

interface ActionPreviewProps {
  action: StagedAction
  icon?: ReactNode
  /** The prerequisite-resolved view the cell is already displaying. */
  view: StagedActionView
  /**
   * A background AskUserQuestion. The card frames the paused run instead of a
   * draft, and opening it is the only move it offers.
   */
  isAnswerable?: boolean
  /** A delivered `report_to_user` message with no work to execute. */
  isReport?: boolean
  /**
   * An archived action. Read-only in the card: body + open, no Edit.
   * Restore lives in the Action View.
   */
  isArchived?: boolean
  isInvoking: boolean
  /** The action has run — the footer carries the result link. */
  isExecuted?: boolean
  /** The row failed to execute — the card carries the message; retry lives on the cell's cluster. */
  hasError?: boolean
  /** Failure needing a (re)connection rather than a plain retry. */
  connectionError?: boolean
  errorMessage?: string | null
  unsatisfiedPrerequisite: boolean
  ctaLabel: string
  onConnectPrerequisite: () => void
  onEdit: () => void
  /** Clears a report from the card; absent once archived (nothing left to clear). */
  onDelete?: () => void
  /** Archives this action after negative feedback succeeds when the user opts in. */
  onArchive?: () => void
  /** Click-through on the card body — opens the Action View, same as the row. */
  onOpen: () => void
}

/**
 * Outbox action variant of the cell hover card (HIG-5838).
 *
 * The card describes the action; acting on it — invoke, archive, undo — lives
 * on the cell's hover button cluster, so the footer carries only feedback and
 * the light CTAs (Figma 8354:21897).
 */
export function ActionPreview({
  action,
  icon,
  view,
  isAnswerable,
  isReport,
  isArchived,
  isInvoking,
  isExecuted,
  hasError,
  connectionError,
  errorMessage,
  unsatisfiedPrerequisite,
  ctaLabel,
  onConnectPrerequisite,
  onEdit,
  onDelete,
  onArchive,
  onOpen,
}: ActionPreviewProps) {
  const [, setFeedbackOpen] = usePinnedDisclosure()
  const feedback = feedbackSubject(action)

  // Stable reference so the memo below can actually hit.
  const nestedViews = view.nestedViews ?? NO_NESTED_VIEWS
  const tags = useMemo(() => applyMetadataTransitions(view.tags, nestedViews), [view.tags, nestedViews])
  const hasBody = tags.length > 0 || !!view.content || nestedViews.length > 0

  // A question has no draft to edit — its only move is opening it. A report has
  // no draft either: it's a delivered message, not a proposal. Archived is
  // read-only here — Restore lives in the Action View.
  const editable = !isArchived && !isExecuted && !isInvoking && !isAnswerable && !isReport

  // A report attributed to a skill is badged with that skill's icon rather than
  // the integration glyph — the skill is what produced the message.
  const reportSkill = getReportOriginSkill(action)
  // Both asks preview their choices; the resolver decides which one this is.
  const options = isAnswerable ? answerableOptionGroups(action, { hideSingleQuestionPrompt: true }) : null

  const actions = isReport ? (
    view.content || onDelete ? (
      <div className="flex items-center gap-1.5">
        {view.content && (
          <CopyIconButton
            value={view.content}
            icon="content"
            ariaLabel="Copy content"
            onCopy={() => trackStagedActionCopy(action, 'content', { trigger: HOVER_CARD_TRIGGER })}
          />
        )}
        {onDelete && (
          <Button variant="primary" left={<ThumbsUp aria-hidden="true" />} onClick={onDelete}>
            Clear
          </Button>
        )}
      </div>
    ) : undefined
  ) : isExecuted ? (
    view.buttons.length > 0 ? (
      <div className="flex items-center gap-1.5">
        {view.buttons.map((button) => (
          // Round outer ends, square join: the group squares off the corners
          // that meet the divider, so `rounded-full` on both segments leaves one
          // pill rather than two — the same idiom as the footer's other controls.
          <ButtonGroup key={button.link} variant="outline">
            <CopyIconButton
              value={button.link}
              icon="link"
              ariaLabel={`Copy link for ${button.label}`}
              className="rounded-full"
              onCopy={() => trackStagedActionCopy(action, 'link', { trigger: HOVER_CARD_TRIGGER })}
            />
            <Button
              variant="outline"
              onClick={() => {
                trackStagedActionOpenLink(action, { trigger: HOVER_CARD_TRIGGER })
                window.open(button.link, '_blank')
              }}
            >
              {button.label}
            </Button>
          </ButtonGroup>
        ))}
      </div>
    ) : undefined
  ) : connectionError && !isInvoking ? (
    <ReconnectButton action={action} />
  ) : unsatisfiedPrerequisite && !isInvoking ? (
    <PrerequisiteCtaButton label={ctaLabel} onClick={onConnectPrerequisite} />
  ) : editable ? (
    <CellPreviewCard.PillButton onClick={onEdit}>
      <Pencil />
      Edit
    </CellPreviewCard.PillButton>
  ) : undefined

  return (
    <CellPreviewCard.Shell
      icon={
        icon ??
        (isAnswerable ? (
          <AskUserBadge variant={askUserCardVariant(action)} />
        ) : reportSkill ? (
          <OriginSkillIcon skillName={reportSkill} />
        ) : (
          getActionIcon(action)
        ))
      }
      title={view.title ?? action.title}
      // With no origin the row is a bare timestamp — nothing for the elbow to
      // attribute the title to.
      sourceConnected={hasActionSourceLabel(action)}
      source={<ActionSource action={action} />}
      feedback={
        feedback ? (
          <CellPreviewCard.FeedbackPill
            {...feedback}
            source="action"
            onOpenChange={setFeedbackOpen}
            onArchive={onArchive}
          />
        ) : undefined
      }
      onOpen={onOpen}
      error={
        hasError && errorMessage ? (
          // Same quiet treatment the Action View uses for its own errors: one
          // line, no icon, no border, no button of its own — the cluster's
          // invoke button is the retry.
          <p className="bg-danger-subtle text-danger-subtle type-ui-xs corner-smooth rounded-lg p-3">{errorMessage}</p>
        ) : undefined
      }
      actions={actions}
    >
      {isAnswerable ? (
        <div className="flex flex-col gap-3">
          <p className="text-foreground-secondary type-prose-sm">{getAnswerableFraming(action)}</p>
          {options && (
            <div className="corner-smooth rounded-lg border p-3">
              <AskUserOptionsPreview {...options} />
            </div>
          )}
        </div>
      ) : (
        hasBody && (
          // The draft itself gets a container, so it reads as the artifact the
          // header describes rather than as more of the header (Figma 8354:21912).
          <div className="corner-smooth flex flex-col gap-3 rounded-lg border p-3">
            {tags.length > 0 && (
              // One line with horizontal scroll, same as the row: metadata is
              // context for the draft below it, so it shouldn't wrap into a
              // block that outweighs the content it describes.
              <ActionTagArea tags={tags} tagOrder={view.tagOrder} singleLine />
            )}
            {visibleNestedViews(nestedViews).map((nested, index) => (
              <NestedView
                key={index}
                view={nested.view}
                preview
                onButtonAction={(target) =>
                  target === 'open link'
                    ? trackStagedActionOpenLink(action, { trigger: HOVER_CARD_TRIGGER, surface: 'nested-view' })
                    : trackStagedActionCopy(action, 'link', { trigger: HOVER_CARD_TRIGGER, surface: 'nested-view' })
                }
              />
            ))}
            {view.content && <ActionContentPreview content={view.content} attachments={view.attachments} clamp />}
          </div>
        )
      )}
    </CellPreviewCard.Shell>
  )
}
